import React from 'react';
import PropTypes from 'prop-types';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import IdChip from '../../common/IdChip';
import ItemTooltip from '../../itemCard/ItemTooltip';
import useTypeMap from '../../../hooks/useTypeMap';
import ClampedText from './ClampedText';
import ApprovePill from './ApprovePill';
import ApprovalReasonDialog from './ApprovalReasonDialog';
import AdminOverrideDialog from './AdminOverrideDialog';
import ApprovalDecisions from './ApprovalDecisions';
import ReviewAssignmentPanel from './ReviewAssignmentPanel';
import CommentThread from './CommentThread';
import CommentComposer from './CommentComposer';
import ItemChildrenList from './ItemChildrenList';
import { deriveApprovalState, computeDescendantsRollup, staleDecisionKind } from './reviewUtils';
import './review.css';

// Heading level = tree depth, capped at 6 — same visual contract as the
// Markdown exporter (spec §Breakdown rendering).
function headingTag(depth) {
  return `h${Math.min(depth + 1, 6)}`;
}
function headingFontSize(depth) {
  if (depth <= 1) return 16;
  if (depth === 2) return 14;
  return 13;
}

/**
 * One requirement/container row in the Breakdown tree: heading (title + id
 * chip + type chip + approve pill), clamped description, optional attributes
 * row, comment-count badge + "+ Comment", the thread (collapsed by default),
 * and — for a container — recursion into its children. Containers get the
 * SAME row (approve/comment controls apply to a container too, spec:
 * "a container can itself be commented on / approved as a grouping").
 */
function ItemBlock({
  node, depth, visibleIds, approvals, threads, expandedThreads, onToggleThread,
  showAttributes, currentUserId = null, openComposerId = null, onOpenComposer, onCloseComposer,
  onApprove, onAddComment, onPatchComment, onReply = null, onEscalate = null,
  onDelete = null, onNavigateToItem = null, composerRef = null,
  onOverrideApproval = null,
  // Item ids whose approval write is in flight (owner ask, 2026-07-28: "The approval
  // button needs to revert to a spinner immediately on click while it's awaiting call
  // response"). A Set rather than a boolean because one BreakdownTab renders a whole
  // SUBTREE of pills and only the clicked one may spin.
  approvingIds = null,
  // The user's friendly-id/raw-UUID preference, threaded from BreakdownTab so this
  // component does not have to subscribe to prefs at every depth of the recursion.
  showHumanIds = undefined,
}) {
  const typeMap = useTypeMap();
  // null | 'reject' | 'override' — which mandatory-text dialog is open (F-06 / F-08).
  const [dialog, setDialog] = React.useState(null);
  // F-60 / REQ-6118 (#606, #629): the reviewer targeted by the admin-override dialog,
  // or null when it's closed. Separate from `dialog` above — that state machine is the
  // CALLER's own decision (reject / approve-over-objection); this one clears SOMEONE
  // ELSE's, a materially different action with its own confirm copy.
  const [overrideTarget, setOverrideTarget] = React.useState(null);
  // F-62 / REQ-6120 (#606, #629): the review-assignment/delegation panel is collapsed
  // by default and fetches only once opened — see ReviewAssignmentPanel's doc comment
  // for why (one GET per item on mount would not scale across a large subtree).
  const [assignOpen, setAssignOpen] = React.useState(false);
  const nodeApprovals = approvals[node.id] || [];
  // F-05 / REQ-6095 (#598): a reviewer's own click must act on THEIR OWN current
  // decision, never the aggregate rollup below — a second reviewer who never
  // touched this item otherwise inherits whatever transition the aggregate implies
  // (silently recorded as rejecting an item they'd never seen). Isolate the
  // caller's own row (`reviewer` is present on every raw approval row this payload
  // carries, `GET /items/{id}/breakdown` — comments_api.py) and run it through the
  // SAME reducer used for the aggregate; a 1-element array collapses to exactly
  // that reviewer's own pending/approved/stale/not_approved. `currentUserId` is the
  // same identity CommentThread already compares against `raised_by`.
  const mineApprovals = currentUserId
    ? nodeApprovals.filter((a) => a && a.reviewer === currentUserId)
    : [];
  const mineState = deriveApprovalState(mineApprovals);
  // F-11 / REQ-6101 (#603): staleness is symmetric, so "stale" alone no longer implies a
  // lapsed APPROVAL. `mineApprovals` holds at most one row (PK `(item_uuid, reviewer)`),
  // so this resolves the caller's own lapsed decision exactly — no guessing needed here,
  // unlike the rollup-only surfaces that depend on the backend's `stale_state`.
  const mineStaleKind = staleDecisionKind(mineApprovals);
  // F-09 / REQ-6099 (#603, D-08): the ITEM-level aggregate. The pill deliberately shows
  // only the caller's own decision (F-05 / REQ-6095, #598) — and because `item_approvals`
  // is keyed `(item_uuid, reviewer)`, a single reviewer's row can never satisfy
  // `deriveApprovalState`'s over-objection rule (it needs a justified fresh approval AND
  // a rejection at the same time). So without this aggregate the state the backend
  // computes, and that D-08 exists to make visible, would render nowhere at all.
  const aggregateState = deriveApprovalState(nodeApprovals);
  const nodeThread = threads[node.id] || [];
  const openCount = nodeThread.filter((c) => c.status === 'open').length;
  const addressedCount = nodeThread.filter((c) => c.status === 'addressed').length;
  const expanded = expandedThreads.has(node.id);
  const composerOpen = openComposerId === node.id;
  const typeLabel = typeMap[node.type_id] || node.type_id || 'Item';
  // Descriptions are stored as markdown (#333) — pass the RAW markdown so
  // ClampedText can render it (#414); the old htmlToText() flattened it to plain
  // text, which showed literal `**`/`- ` markup.
  const description = node.description || '';
  const attrEntries = node.attributes ? Object.entries(node.attributes) : [];

  // F-08 / REQ-6098 (#603, D-08): OTHER reviewers whose rejection is live against the
  // current content. A STALE rejection is excluded deliberately — it no longer blocks,
  // so approving past it is not an override and must not demand a justification. This
  // mirrors `_open_objections` in core/requirements/feedback.py; the client asks for
  // the justification up front so the reviewer is not told to supply one by a 400.
  const openObjections = nodeApprovals.filter(
    (a) => a && a.state === 'not_approved' && !a.stale && a.reviewer !== currentUserId,
  );

  const requestApprove = () => {
    // Approving while someone else's fresh rejection stands SUPERSEDES it, which the
    // backend accepts only with a justification (400 without) and only from a holder
    // of the `approver` role (403 without) — so collect it first.
    if (openObjections.length > 0) { setDialog('override'); return; }
    onApprove(node.id, 'approved');
  };

  const handleApproveClick = () => {
    // F-05 / REQ-6095 (#598): keyed off the CALLER's own state (`mineState`), never
    // the aggregate — see the comment on `mineApprovals` above.
    // A stale approval's own click just re-confirms it (refreshes the hash
    // server-side) — it does not advance to "not approved" (spec §Approve toggle).
    if (mineState === 'stale') { requestApprove(); return; }
    if (mineState === 'pending') requestApprove();
    // F-06 / REQ-6096 (#603, D-09): rejecting REQUIRES a reason — collect it before
    // the write. Before this the click fired a reasonless PUT that the core rejected
    // with a 400, so the pill silently failed to advance.
    // NB `approved_over_objection` is deliberately NOT a case here: `mineApprovals` is a
    // single row, so `deriveApprovalState` can never return it for one reviewer (it needs
    // a justified fresh approval AND a rejection together). The contested state surfaces
    // via `aggregateState` / the objection banner instead.
    else if (mineState === 'approved') setDialog('reject');
    else onApprove(node.id, null); // not_approved -> pending (clear)
  };

  const confirmDialog = (text) => {
    const mode = dialog;
    setDialog(null);
    if (mode === 'reject') onApprove(node.id, 'not_approved', { reason: text });
    else onApprove(node.id, 'approved', { justification: text });
  };

  const visibleChildren = (node.children || []).filter((c) => visibleIds.has(c.id));
  // F-15 / REQ-6105 (#599, D-02): a container's own approval (the pill above, keyed
  // off `mineState`) is independent of its descendants (F-14) — so it additionally
  // carries a SEPARATE, visually-distinct summary of how its subtree is doing. Text
  // chip (not a second coloured pill) so it can never be mistaken for the node's own
  // approve/not-approve state. `node.children` (raw, not `visibleChildren`) so the
  // count reflects the true subtree regardless of the active Breakdown filter.
  const hasChildren = (node.children || []).length > 0;
  const descendantsRollup = hasChildren ? computeDescendantsRollup(node, approvals) : null;

  return (
    <div
      className={`bditem d${Math.min(depth, 3)}`}
      style={{ '--depth': String(depth - 1) }}
      data-testid={`item-block-${node.id}`}
    >
      {/* Title + id/type chips are ONE wrapping group on the left; the approve
          pill is pinned top-right and never wraps under (spec review: "approve
          pill always on the right, with the id + item type flowing under the
          title if there's not enough width") — mock's `.bdhead` (#450). */}
      <div className="bdhead" data-testid={`item-header-${node.id}`}>
        {/* The wrapping group the comment above has always described. It did not exist:
            title, chips and pill were all direct flex children of a `flex-wrap: wrap`
            row, so on a narrow column or a long title the PILL wrapped to its own line
            and the "always on the right" rule silently stopped holding — the owner's
            report that the approve button "is in some instances not rendered on the same
            row as the title etc. ... and I doubt is to spec". It wasn't. Chips now flow
            under the title inside this group; the pill is a sibling that cannot wrap. */}
        <div className="bdhead-main">
          {React.createElement(
            headingTag(depth),
            { className: 'htitle', style: { fontSize: headingFontSize(depth) } },
            node.title,
          )}
          {/* Standardised id display (#174) — same idiom as DocumentTab, whose comment
              already promised "the raw GUID is never shown inline". This surface never
              got it: `IdChip` was rendered with no `showHumanIds`, which falls through
              to the legacy ad-hoc branch and prints a truncated UUID regardless of the
              user's preference, with only the plain MUI tooltip. That is the owner's
              "I don't know why GUIDs are shown against item titles ... must follow the
              preference set in the user's settings, and also always display the advanced
              tooltip (it doesn't currently)". Both halves are here: the pref decides the
              label, ItemTooltip supplies the hover mini-card + navigation. */}
          <ItemTooltip item={{
            id: node.id, display_id: node.display_id, title: node.title, type: node.type,
          }}
          >
            <IdChip
              id={node.id}
              displayId={node.display_id}
              humanId={node.human_id}
              showHumanIds={showHumanIds}
              title={node.title || ''}
              disableTooltip
              onClick={() => onNavigateToItem && onNavigateToItem(node.id)}
            />
          </ItemTooltip>
          <span className="rmdv-typechip">{typeLabel}</span>
        {/* F-15 / REQ-6105 (#599, D-02): descendant-rollup summary — a compact TEXT
            chip, deliberately not styled like ApprovePill, so it reads as "subtree
            status" rather than a second verdict on this node's own approval. Hidden
            when there's nothing to roll up (a leaf, or a container with no loaded
            children yet). */}
        {descendantsRollup && descendantsRollup.total > 0 && (
          <span
            className="rmdv-descendant-rollup"
            data-testid={`descendant-rollup-${node.id}`}
            title={`Subtree: ${descendantsRollup.approved} approved, ${descendantsRollup.stale} stale, ${descendantsRollup.not_approved} not approved, ${descendantsRollup.pending} pending, of ${descendantsRollup.total} descendant${descendantsRollup.total === 1 ? '' : 's'} — independent of this container's own approval`}
          >
            {`⊆ ${descendantsRollup.approved}/${descendantsRollup.total} descendants approved`}
          </span>
        )}
        </div>
        {/* Outside `.bdhead-main`, so it is pinned right and cannot wrap under the
            title. `.grow` is gone with it — the spacer only existed to push the pill
            across a row it now sits outside of. */}
        <ApprovePill
          state={mineState}
          staleState={mineStaleKind}
          onClick={handleApproveClick}
          busy={!!(approvingIds && approvingIds.has(node.id))}
        />
      </div>

      {/* F-09 / REQ-6099 (#603, D-08): the item reads "approved over objection" — a state
          the caller's own pill above structurally cannot show (see `aggregateState`).
          Named separately from the pill so a reader can tell "I approved this" from
          "this item's sign-off is contested". */}
      {aggregateState === 'approved_over_objection' && (
        <div
          className="rmdv-objection-banner"
          data-testid={`objection-banner-${node.id}`}
          title="An approver with authority signed this off over another reviewer’s rejection — both decisions are retained below"
        >
          <span aria-hidden="true">*</span>
          Approved over an objection — both decisions are retained
        </div>
      )}

      {/* F-06 / REQ-6096 + F-08 / REQ-6098 (#603): the mandatory rationale /
          justification the backend requires before it will record the decision. */}
      <ApprovalReasonDialog
        open={dialog !== null}
        mode={dialog === 'override' ? 'override' : 'reject'}
        itemTitle={node.title || node.id}
        objectors={openObjections.map((a) => a.actor || a.reviewer)}
        onCancel={() => setDialog(null)}
        onConfirm={confirmDialog}
      />

      {/* F-06 / F-08 / F-09 / F-12 / F-52: the recorded decisions themselves — reason,
          justification, both parties, revision provenance, self-approval marker. WP1
          captured every one of these and nothing displayed them. F-60/F-61 (#606,
          #629): the override entry point and the "user inactive" annotation live
          inside this component too. */}
      <ApprovalDecisions
        rows={nodeApprovals}
        currentVersion={typeof node.version === 'number' ? node.version : null}
        itemId={node.id}
        onOverride={onOverrideApproval ? (reviewer) => setOverrideTarget(reviewer) : null}
      />

      {/* F-60 / REQ-6118 (#606, #629): mandatory, non-empty reason before an admin
          override can be submitted — mirrors ApprovalReasonDialog's own pattern
          (see AdminOverrideDialog's doc comment for why it is a separate component). */}
      {onOverrideApproval && (
        <AdminOverrideDialog
          open={overrideTarget !== null}
          reviewer={overrideTarget}
          itemTitle={node.title || node.id}
          onCancel={() => setOverrideTarget(null)}
          onConfirm={(reason) => {
            const target = overrideTarget;
            setOverrideTarget(null);
            onOverrideApproval(node.id, target, reason);
          }}
        />
      )}

      <ClampedText text={description} />

      {showAttributes && attrEntries.length > 0 && (
        <div className="bdattrs" data-testid={`item-attrs-${node.id}`}>
          {attrEntries.map(([k, v]) => (
            <span key={k}>
              {k}
              {' '}
              <b>{v && typeof v === 'object' ? JSON.stringify(v) : String(v)}</b>
            </span>
          ))}
        </div>
      )}

      <div className="bdctl">
        {nodeThread.length > 0 && (
          <button
            type="button"
            onClick={() => onToggleThread(node.id)}
            aria-expanded={expanded}
            data-testid={`comment-count-${node.id}`}
            className={`cmtcount${openCount > 0 ? ' open' : ''}${expanded ? ' expanded' : ''}`}
          >
            <ChevronRightIcon className="chev" sx={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }} />
            <ChatBubbleOutlineIcon className="ic" />
            {openCount > 0 ? `${openCount} open` : `${addressedCount || nodeThread.length} addressed`}
          </button>
        )}
        {/* mock's `.bdctl` "+ Comment" is a plain `.linkbtn` (docs/design/specimen/
            review.html) — NOT a filled/primary button (#450 reopen: match the
            mock's actual DOM/chrome, not just its colour tokens). */}
        {!composerOpen && (
          <button
            type="button"
            className="linkbtn"
            onClick={() => onOpenComposer(node.id)}
            data-testid={`add-comment-${node.id}`}
          >
            + Comment
          </button>
        )}
        {/* F-62 / REQ-6120 (#606, #629): the review-assignment/delegation admin
            surface — collapsed by default (see ReviewAssignmentPanel's doc comment
            for why it's lazy-fetched rather than mounted for every row up front). */}
        <button
          type="button"
          className="linkbtn muted"
          onClick={() => setAssignOpen((v) => !v)}
          data-testid={`review-assignment-toggle-${node.id}`}
        >
          {assignOpen ? 'Hide review assignment' : 'Review assignment'}
        </button>
      </div>

      {assignOpen && <ReviewAssignmentPanel itemId={node.id} currentUserId={currentUserId} />}

      {expanded && nodeThread.length > 0 && (
        <CommentThread
          comments={nodeThread}
          currentUserId={currentUserId}
          onAction={(commentId, action, opts) => onPatchComment(node.id, commentId, action, opts)}
          onReply={onReply ? (parentId, body) => onReply(node.id, parentId, body) : null}
          onEscalate={onEscalate ? (commentId, fields) => onEscalate(node.id, commentId, fields) : null}
          onDelete={onDelete ? (commentId) => onDelete(node.id, commentId) : null}
        />
      )}

      {composerOpen && (
        <CommentComposer
          ref={composerRef}
          placeholder={`Leave feedback on ${node.title || node.id} — plain text, visible to the agent via get_review_feedback().`}
          onCancel={onCloseComposer}
          onSave={async (body) => {
            await onAddComment(node.id, body, { kind: 'item' });
            onCloseComposer();
          }}
        />
      )}

      {/* N-20 / REQ-6132 (#596): cap how many of this container's OWN children
          mount at once — see ItemChildrenList's doc comment. `resetKey`/testid
          keyed on this node so a container deep in the tree gets its own
          independent "show more" state. */}
      <ItemChildrenList
        items={visibleChildren}
        resetKey={node.id}
        testIdSuffix={node.id}
        renderItem={(child) => (
          <ItemBlock
            key={child.id}
            node={child}
            depth={depth + 1}
            visibleIds={visibleIds}
            approvals={approvals}
            threads={threads}
            expandedThreads={expandedThreads}
            onToggleThread={onToggleThread}
            showAttributes={showAttributes}
            currentUserId={currentUserId}
            openComposerId={openComposerId}
            onOpenComposer={onOpenComposer}
            onCloseComposer={onCloseComposer}
            onApprove={onApprove}
            onAddComment={onAddComment}
            onPatchComment={onPatchComment}
            onReply={onReply}
            onEscalate={onEscalate}
            onDelete={onDelete}
            onNavigateToItem={onNavigateToItem}
            composerRef={composerRef}
            onOverrideApproval={onOverrideApproval}
            // Both must recurse: a child's pill needs to spin on ITS click, and a child's
            // id chip has to honour the same preference as its parent's. Dropping either
            // here would make the fix hold only at depth 0 — which is exactly the shape
            // of bug that gets reported as "it works sometimes".
            approvingIds={approvingIds}
            showHumanIds={showHumanIds}
          />
        )}
      />
    </div>
  );
}

ItemBlock.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    attributes: PropTypes.object,
    type_id: PropTypes.string,
    type: PropTypes.string,
    display_id: PropTypes.string,
    human_id: PropTypes.string,
    children: PropTypes.array,
  }).isRequired,
  // Ids whose approval write is in flight — that pill renders a spinner.
  approvingIds: PropTypes.instanceOf(Set),
  // The user's friendly-id/raw-UUID preference (#174), threaded from BreakdownTab.
  showHumanIds: PropTypes.bool,
  depth: PropTypes.number.isRequired,
  visibleIds: PropTypes.instanceOf(Set).isRequired,
  approvals: PropTypes.object.isRequired,
  threads: PropTypes.object.isRequired,
  expandedThreads: PropTypes.instanceOf(Set).isRequired,
  onToggleThread: PropTypes.func.isRequired,
  showAttributes: PropTypes.bool.isRequired,
  currentUserId: PropTypes.string,
  openComposerId: PropTypes.string,
  onOpenComposer: PropTypes.func.isRequired,
  onCloseComposer: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onAddComment: PropTypes.func.isRequired,
  onPatchComment: PropTypes.func.isRequired,
  onReply: PropTypes.func,
  onEscalate: PropTypes.func,
  onDelete: PropTypes.func,
  onNavigateToItem: PropTypes.func,
  composerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  // F-60 / REQ-6118 (#606, #629): (itemId, reviewer, reason) => void. Omitted hides
  // the Override control entirely (ApprovalDecisions' onOverride prop is only wired
  // when this is supplied) — mirrors onReply/onEscalate/onDelete's optional pattern.
  onOverrideApproval: PropTypes.func,
};

export default ItemBlock;
